import React, { useState } from "react";
import { base44 } from "../../api/base44Client";
import { useMutation } from "@tanstack/react-query";
import { Upload, X, Loader2 } from "lucide-react";

export default function ImageUpload({ 
  label = "Image", 
  value, 
  onChange, 
  multiple = false,
  maxSize = 5
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const result = await base44.integrations.Core.UploadFile({ file });
      return result.url || result.file_url;
    },
    onSuccess: (url) => {
      if (multiple) {
        const currentUrls = Array.isArray(value) ? value : [];
        onChange([...currentUrls, url]);
      } else {
        onChange(url);
      }
      setUploading(false);
      setError("");
    },
    onError: () => {
      setError("Erreur lors du téléchargement");
      setUploading(false);
    }
  });

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > maxSize * 1024 * 1024) {
        setError(`Fichier trop volumineux (max ${maxSize}MB)`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        setError(`${file.name} n'est pas une image valide`);
        continue;
      }
      setUploading(true);
      setError("");
      await uploadMutation.mutateAsync(file);
    }
  };

  const removeImage = (urlToRemove) => {
    if (multiple) {
      onChange(value.filter(url => url !== urlToRemove));
    } else {
      onChange("");
    }
  };

  const urls = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-black transition-colors">
        <input
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
          id={`file-upload-${label}`}
        />
        <label htmlFor={`file-upload-${label}`} className="flex flex-col items-center justify-center cursor-pointer">
          {uploading ? (
            <>
              <Loader2 className="w-12 h-12 text-black animate-spin mb-3" />
              <p className="text-sm text-gray-600">Téléchargement en cours...</p>
            </>
          ) : (
            <>
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">
                Cliquez pour sélectionner {multiple ? "des images" : "une image"}
              </p>
              <p className="text-xs text-gray-500">PNG, JPG, JPEG (max {maxSize}MB)</p>
            </>
          )}
        </label>
      </div>
      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}
      {urls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {urls.map((url, index) => (
            <div key={index} className="relative group">
              <img src={url} alt={`Image ${index + 1}`} className="w-full h-32 object-cover rounded-lg border-2 border-gray-200" />
              <button
                type="button"
                onClick={() => removeImage(url)}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
